import type { Scale } from '@tunasong/schemas'
import type { Chord, ChordVariant } from '../chord/index.js'
import { isChordNotes } from '../chord/chord-types.js'
import { getFunctionCategory } from './function-quiz.js'
import { getTritoneSubFunction } from './tritone-sub.js'
import type { ChordAnalysis, ChordFunction } from './types.js'

/**
 * Tension Contour Stripe — harmonic-tension model (Bowie-locked).
 *
 * Computes one per-chord tension scalar (0..1) plus an a11y bucket. The stripe
 * UI (Joni-designed, a SEPARATE later task) plots these across a progression so
 * the songwriter can *see* the rise-and-fall of a phrase. This module is the
 * music-theoretic core only — no UI.
 *
 * Model: docs/superpowers/specs/2026-06-03-tension-contour-stripe-model-spec.md
 *
 * `tension = clamp(L1 × modalFactor + L2 + L3, 0, 1)`
 *   L1 — functional base (the T→P→D arc), the only layer modal attenuation touches
 *   L2 — chordal dissonance (intrinsic; idiom-independent)
 *   L3 — chromatic function (borrowed / secondary-dominant / tritone-sub)
 */

export const tensionBuckets = ['rest', 'low', 'medium', 'high', 'peak'] as const
export type TensionBucket = (typeof tensionBuckets)[number]

export interface HarmonicTensionInput {
  /** The chord whose tension to compute (quality + extensions drive Layer 2). */
  chord: Chord
  /** Analyzer output — `chordFunction` + `borrowing` drive Layers 1 & 3. */
  analysis: ChordAnalysis
  /** Key/scale context — drives Layer 4 modal attenuation. Absent ⇒ functional. */
  scale?: Scale
  /** The following chord, when known — lets tritone-sub detection fire (Layer 3). */
  next?: Chord
}

export interface HarmonicTension {
  /** Tension scalar in [0, 1]. */
  value: number
  /** a11y-redundant bucket the UI maps to a cool→warm ramp + non-colour channel. */
  bucket: TensionBucket
  /** True for unanalyzable chords rendered at the muted medium default (§5). */
  lowConfidence?: boolean
}

/**
 * Layer-1 functional base per chord-function label (model spec §3). Labels not
 * listed here fall back to their T/P/D category base via `CATEGORY_BASE`.
 */
const FUNCTION_BASE: Partial<Record<ChordFunction, number>> = {
  // Tonic (rest)
  I: 0.0,
  i: 0.0,
  // Tonic substitute
  vi: 0.15,
  iii: 0.15,
  '♭III': 0.15,
  '♭VI': 0.15,
  '♭III+': 0.15,
  // Predominant
  IV: 0.35,
  iv: 0.35,
  // Predominant (ii-family — Layer 1 is QUALITY-FREE: ii°'s diminished
  // dissonance is Layer 2's job, counted once. §8 revision 2026-06-03.)
  ii: 0.4,
  'ii°': 0.4,
  // Predominant (Neapolitan — chromaticism is Layer 3's job via `borrowing`,
  // counted once: 0.40 + 0.10 borrowed = 0.50 medium.)
  '♭II': 0.4,
  // Dominant
  V: 0.65,
  // Dominant (leading-tone — quality-free FUNCTIONAL base below V; the
  // diminished triad's +0.12 lifts the plain vii° to 0.70 (just above V, the
  // classic reading) and °7's +0.18 to 0.76 — both `high`. `peak` stays
  // reserved for chromatic intensification. §8 revision 2026-06-03.)
  'vii°': 0.58,
}

/** Category-level fallback base for labels absent from `FUNCTION_BASE`. */
const CATEGORY_BASE = { tonic: 0.0, predominant: 0.35, dominant: 0.65 } as const

/** Layer-1 base reset for any secondary dominant / tritone sub: it IS a dominant. */
const SECONDARY_DOMINANT_BASE = 0.65

/** Bucket thresholds (model spec §3). Upper bounds exclusive except `peak`. */
const bucketFor = (value: number): TensionBucket => {
  if (value < 0.12) {
    return 'rest'
  }
  if (value < 0.3) {
    return 'low'
  }
  if (value < 0.55) {
    return 'medium'
  }
  if (value < 0.8) {
    return 'high'
  }
  return 'peak'
}

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n))

/** Round to 5 decimals to keep additive float drift out of the public value. */
const round5 = (n: number): number => Math.round(n * 1e5) / 1e5

/** Mode scale types that attenuate the functional layer (model spec §4). */
const MODAL_SCALE_TYPES: ReadonlySet<string> = new Set([
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'aeolian',
  'locrian',
])

/**
 * `modalFactor` — multiplies ONLY Layer 1. 1.0 functional, 0.4 modal.
 *
 * Modal = the scale type is a mode (Dorian/Phrygian/Lydian/Mixolydian/Aeolian-
 * as-modal-vamp/Locrian). `ionian` is plain major (functional); `major`,
 * `minor` (natural-minor KEY — harmonic-minor lift in practice),
 * `harmonicminor`, `melodicminor`, and the pentatonics are functional.
 * Undetermined (no scale) defaults to 1.0.
 */
const modalFactor = (scale: Scale | undefined): number => {
  if (!scale?.type) {
    return 1.0
  }
  return MODAL_SCALE_TYPES.has(scale.type) ? 0.4 : 1.0
}

const isChromaticOutlier = (chordFunction: ChordFunction): boolean => chordFunction === 'chromatic'

/** A true secondary dominant (`V/X`) — NOT a tritone sub (`subV/X`, Layer-3). */
const isSecondaryDominantLabel = (chordFunction: ChordFunction): boolean => chordFunction.startsWith('V/')

/** Resolve Layer-1 functional base for a chord-function label. */
const functionalBase = (chordFunction: ChordFunction): number => {
  const explicit = FUNCTION_BASE[chordFunction]
  if (explicit !== undefined) {
    return explicit
  }
  const category = getFunctionCategory(chordFunction)
  if (category) {
    return CATEGORY_BASE[category]
  }
  // Unknown-but-not-flagged label: treat as a mild predominant departure rather
  // than a false rest. (Genuinely unanalyzable chords are handled upstream.)
  return CATEGORY_BASE.predominant
}

/** Count of altered tones on a dominant variant — drives the +0.06-each cap. */
const ALTERATION_BY_VARIANT: Record<string, number> = {
  '7b5': 1,
  '7#5': 1,
  '7b9': 1,
  '7#9': 1,
  '+7': 1, // aug7 alias of 7#5
}

/**
 * Layer-2 chordal dissonance from the chord's quality + extensions (model spec
 * §3 table). Power chords (no 3rd) and note-list chords contribute nothing.
 * The extension/alteration contribution is additionally capped at +0.20.
 */
const chordalDissonance = (chord: Chord): number => {
  if (isChordNotes(chord)) {
    return 0
  }
  /**
   * GH #1510: some persisted documents carry chords with `variant: null`
   * (violating ChordVariant). `isChordVariant` now rejects those at the type
   * boundary, but `computeHarmonicTension` is exported and reachable from
   * other guards — coalesce so the string ops below can't crash. A null/absent
   * variant carries no quality information: score it as a plain triad (`''`).
   */
  const variant: ChordVariant['variant'] = chord.variant ?? ''

  // Core quality (mutually exclusive triad/seventh families).
  let core = 0
  if (variant === 'aug' || variant.startsWith('aug')) {
    core = 0.18
  } else if (variant === 'dim7' || variant === 'dimmaj7') {
    core = 0.18
  } else if (variant === 'dim' || variant.startsWith('dim')) {
    core = 0.12
  } else if (variant === 'm7b5' || variant === 'ø' || variant === 'ø7' || variant === 'min7b5') {
    core = 0.12
  } else if (variant === '7' || variant.startsWith('7') || variant === '+7') {
    core = 0.1 // dominant 7 — the tritone
  } else if (variant === 'maj7' || variant === 'Δ' || variant === 'M7' || variant === 'mmaj7') {
    core = 0.05
  } else if (variant === 'm7') {
    core = 0.05
  }

  // Suspensions (mild — the suspended tone wants to resolve).
  let sus = 0
  if (variant.includes('sus')) {
    sus = 0.06
  }

  // Extension colour (capped +0.20 with the alterations below).
  // Added-sixth: literal 6 / m6 / 69-family qualities ONLY — never a substring
  // match, which would wrongly fire on aug6-type chromatic chords (§8, flag 3).
  let extension = 0
  if (/^(m|maj|min)?6(\/?9)?$/.test(variant)) {
    extension += 0.03
  }
  if (variant === '9' || variant.includes('add9')) {
    extension += 0.05
  }
  if (variant.includes('11')) {
    extension += 0.08
  }
  if (variant.includes('13')) {
    extension += 0.1
  }

  // Altered dominant tones — +0.06 each.
  const alterations = (ALTERATION_BY_VARIANT[variant] ?? 0) * 0.06

  const cappedExtension = Math.min(0.2, extension + alterations)
  return core + sus + cappedExtension
}

/**
 * Compute per-chord harmonic tension. Pure + deterministic.
 *
 * Edge cases (§5):
 *  - Unanalyzable (`chordFunction === 'chromatic'`) ⇒ value 0.40, bucket
 *    `medium`, `lowConfidence: true` — an unknown chord is NOT rest.
 *  - Power chords / note-list chords ⇒ Layer 1 only (Layer 2 = 0).
 */
export const computeHarmonicTension = (input: HarmonicTensionInput): HarmonicTension => {
  const { chord, analysis, scale, next } = input
  const { borrowing } = analysis
  // `analysis` derives from persisted @tunasong/models suggestion data, which can
  // carry a null `chordFunction` (legacy/malformed rows violating the type). A
  // null function carries no functional information → treat as unanalyzable so
  // the `.startsWith` label tests below can't crash (TUNASONG-W0; sibling of the
  // `variant: null` coalesce in #1510).
  const chordFunction = analysis.chordFunction ?? 'chromatic'

  if (isChromaticOutlier(chordFunction)) {
    return { value: 0.4, bucket: 'medium', lowConfidence: true }
  }

  // Layer 3 chromatic detection (also influences Layer 1 for secondary doms).
  // The `subV/` label is authoritative when present; otherwise consult the
  // tritone-sub analyzer, which needs both a scale and a `next` resolution.
  const isTritoneSub =
    chordFunction.startsWith('subV/') ||
    (scale !== undefined && getTritoneSubFunction(chord, scale, next) !== null)
  const isSecondaryDominant = isSecondaryDominantLabel(chordFunction) || borrowing === 'secondary-dominant'
  const isBorrowed = borrowing === 'parallel-mode'

  // Layer 1 — functional base (secondary doms / tritone subs reset to dominant).
  const base =
    isSecondaryDominant || isTritoneSub ? SECONDARY_DOMINANT_BASE : functionalBase(chordFunction)

  // Layer 4 — modal attenuation multiplies ONLY Layer 1.
  const layer1 = base * modalFactor(scale)

  // Layer 2 — intrinsic chordal dissonance (idiom-independent).
  const layer2 = chordalDissonance(chord)

  // Layer 3 — chromatic function additions.
  let layer3 = 0
  if (isBorrowed && !isSecondaryDominant) {
    layer3 += 0.1
  }
  if (isSecondaryDominant) {
    layer3 += 0.1
  }
  if (isTritoneSub) {
    layer3 += 0.2
  }

  const value = round5(clamp01(layer1 + layer2 + layer3))
  return { value, bucket: bucketFor(value) }
}
